import { memo, Fragment, useCallback } from 'react'
import { Group, Button, Badge, Text, Divider, Tooltip } from 'components'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { useBrowseTree } from './context/BrowseTreeContext'

const LINK_COLOR = '#5B4FEB'
const LINK_HOVER_COLOR = '#7269EF'
const DEFAULT_BADGE_COLOR = '#625F6D'

export const BrowseTreeBreadcrumb = memo(() => {
  const { t } = useTranslation()
  const { navigationPath, navigateBack, navigateToBreadcrumb } = useBrowseTree()

  const handleNavigate = useCallback((level: number) => {
    navigateToBreadcrumb(level)
  }, [navigateToBreadcrumb])

  if (navigationPath.length === 0) return null

  return (
    <Group
      spacing={12}
      sx={theme => ({
        padding: '12px 16px',
        backgroundColor: theme.colorScheme === 'dark'
          ? theme.colors.dark[6]
          : theme.colors.gray[1],
        borderRadius: theme.radius.md,
        flexWrap: 'nowrap',
        overflowX: 'auto',
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.colors.gray[4],
          borderRadius: 2,
        },
      })}
    >
      <Button
        variant="subtle"
        size="sm"
        compact
        leftIcon={<IconChevronLeft size={16} stroke={2} />}
        onClick={navigateBack}
        sx={theme => ({
          color: theme.colors.teal[7],
          fontWeight: 400,
          fontSize: 14,
          height: 32,
          padding: '0 12px',
          flexShrink: 0,
          '&:hover': {
            backgroundColor: theme.colors.teal[0],
            color: theme.colors.teal[8],
          },
        })}
      >
        {t('arc_browse_back')}
      </Button>

      <Divider orientation="vertical" sx={{ height: 20, alignSelf: 'center', flexShrink: 0 }} />

      {navigationPath.map((item, index) => (
        <Fragment key={`${item.id}-${index}`}>
          {index === 0 ? (
            <Tooltip label={item.name} position="bottom" withArrow openDelay={300}>
              <Badge
                variant="filled"
                size="sm"
                sx={theme => ({
                  fontWeight: 500,
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: 0.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '90%',
                  flexShrink: 1,
                  backgroundColor: item.color || DEFAULT_BADGE_COLOR,
                  color: theme.white,
                })}
              >
                {item.name}
              </Badge>
            </Tooltip>
          ) : (
            <Tooltip label={item.name} position="bottom" withArrow openDelay={300}>
              <Text
                size="sm"
                sx={{
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '90%',
                  flexShrink: 1,
                  color: LINK_COLOR,
                  '&:hover': {
                    textDecoration: 'underline',
                    color: LINK_HOVER_COLOR,
                  },
                }}
                onClick={() => handleNavigate(index + 1)}
              >
                {item.name}
              </Text>
            </Tooltip>
          )}
          {index < navigationPath.length - 1 && (
            <IconChevronRight size={16} color="#868e96" style={{ flexShrink: 0 }} />
          )}
        </Fragment>
      ))}
    </Group>
  )
})

BrowseTreeBreadcrumb.displayName = 'BrowseTreeBreadcrumb'
